import { DateTime } from 'luxon';
import { reservationRepository } from '@/service/repository/reservationRepository';
import { serviceEventRepository } from '@/service/repository/serviceEventRepository';
import { copilotRepository } from '@/service/repository/copilotRepository';
import { groupItemsBySkuAndChoices, hideAllOffcanvas } from '@/utils';
import { filterNotificationsByPlayableGate } from '@/utils/notificationGate';

/**
 * Touches every Vuex state field the playable gate reads via store.getters, so
 * `notifications` / `assistantNotifications` recompute when filters change.
 */
function playableGateReactiveDeps(state, getters) {
	return {
		selectedRole: getters.getSelectedRole,
		selectedZones: Array.isArray(state.selectedZoneFilter)
			? state.selectedZoneFilter.filter((z) => typeof z === 'string')
			: [],
		allowedTables: getters.selectedRangeTableNames || [],
		selectedStations: state.selectedStations,
		excludedStations: state.excludedStations,
		showAllProductOrdersInKitchen: state.showAllProductOrdersInKitchen,
		onlyProductOrdersWithoutElaborationStation: state.onlyProductOrdersWithoutElaborationStation,
		showProductOrdersWithoutElaborationWhenExcludingOnly:
			state.showProductOrdersWithoutElaborationWhenExcludingOnly,
	};
}
import { cookieManager } from '@/service/cookieManager';
import { deviceManager } from '@/service/deviceManager';
import { productOrderService } from '@/service/ProductOrderService';
import { configRepository } from '@/service/repository/configRepository';
import { useNotificationStore } from '@/stores/notificationStore';
import { DELIVERY_STATUS } from '@/constants/deliveryStatus';
import { TAKEAWAY_STATUS } from '@/constants/takeawayStatus';
import {
	isKitchenZoneRangeFilterActive,
	reservationPassesKitchenZoneRangeFilter,
} from '@/utils/copilotReservationZoneRangeFilter.js';
import { venueRoomLabel } from '@/utils/venueRoomLabel';
import { isLocatedBarMode } from '@/utils/venueRoomServiceMode';
import { isTpvPlusBarraRole, roleUsesDrinksPartidaFilter } from '@/constants/copilotRoleNames';
import { collectBarraElaborationStations } from '@/utils/elaborationStations';
import {
	loadCopilotStationFilterSnapshot,
	applyStationFilterSnapshotToCopilotState,
	saveCopilotStationFilterSnapshot,
	defaultStationFilterSnapshot,
	normalizeShowAllProductOrdersWithStationFilters,
} from '@/service/copilotStationFilterStorage';
import { normalizeServiceEvent, normalizeServiceEvents } from '@/utils/serviceEventNormalizer';
import {
	appendUnassignedZoneIfNeeded,
	collectReservationTableNames,
	isUnassignedReservation,
	reservationMatchesZoneOrRangeFilter,
} from '@/utils/unassignedReservationZone';
import { filterRolesForStaff, getStaffVenueAssignment, normalizeRoleId } from '@/utils/staffRoles';

const COPILOT_TPV_MARK_SERVED_SESSION_KEY = 'copilot-tpv-mark-served';

/**
 * Whether an optimistic (fictitious) card is the placeholder for this real reservation.
 * Used so WS/createWalkin inserts can drop the skeleton before the real card appears.
 */
function fictitiousAccountMatchesReservation(fictitious, reservation) {
	if (!fictitious?.isFictitious || !reservation) {
		return false;
	}

	const fictitiousTables = collectReservationTableNames(fictitious);
	const reservationTables = collectReservationTableNames(reservation);
	if (fictitiousTables.size > 0 && reservationTables.size > 0) {
		for (const name of fictitiousTables) {
			if (reservationTables.has(name)) {
				return true;
			}
		}
		return false;
	}

	const sameRoom =
		String(fictitious.roomName || '').toLowerCase() === String(reservation.roomName || '').toLowerCase();
	const fictitiousTag = fictitious.locationTag != null && String(fictitious.locationTag).trim() !== ''
		? String(fictitious.locationTag).trim()
		: null;
	const reservationTag = reservation.locationTag != null && String(reservation.locationTag).trim() !== ''
		? String(reservation.locationTag).trim()
		: null;

	if (fictitiousTag && reservationTag) {
		return sameRoom && fictitiousTag === reservationTag;
	}

	// Walk-in / no table: only match another table-less placeholder in the same room
	if (fictitiousTables.size === 0 && reservationTables.size === 0) {
		return sameRoom;
	}

	return false;
}

function readInitialTpvMarkServedShiftMode() {
	if (typeof sessionStorage === 'undefined') {
		return false;
	}
	try {
		return sessionStorage.getItem(COPILOT_TPV_MARK_SERVED_SESSION_KEY) === '1';
	} catch {
		return false;
	}
}

function readInitialSelectedAreaFilter() {
	try {
		const saved = localStorage.getItem('selectedAreaFilter');
		if (saved === 'table' || saved === 'hour') {
			return saved;
		}
	} catch {
		// ignore
	}
	return 'hour';
}

function parseFinishedDateTime(finishedDateTime) {
	if (!finishedDateTime) return null;
	if (typeof finishedDateTime === 'string') return finishedDateTime;
	if (finishedDateTime.date) return finishedDateTime.date;
	return null;
}

/**
 * When the backend returns serviceMode=null on a reservation, infer it from the
 * tables slice (populated from initData.rooms on every app load).
 *
 * Strategy (most to least precise):
 *  1. Match by reservation.tables[0].id  → store table entry → serviceMode
 *  2. Match by reservation.roomName      → store table entry → roomServiceMode
 */
function enrichReservationsServiceMode(reservations, tables) {
	if (!Array.isArray(reservations)) return reservations;
	return reservations.map((r) => {
		if (r.serviceMode != null) return r;

		// 1. Table UUID match — most reliable
		const firstTableId = r.tables?.[0]?.id;
		if (firstTableId) {
			const byId = tables.find((t) => (t.id ?? t.table_id) === firstTableId);
			if (byId?.serviceMode) return { ...r, serviceMode: byId.serviceMode };
			if (byId?.roomServiceMode) return { ...r, serviceMode: byId.roomServiceMode };
		}

		// 2. Room name fallback — covers walkins without a specific table yet
		const roomName = r.roomName;
		if (roomName) {
			const byRoom = tables.find((t) => t.room === roomName);
			if (byRoom?.roomServiceMode) return { ...r, serviceMode: byRoom.roomServiceMode };
		}

		return r;
	});
}

function commitReservationsToStore(commit, reservations) {
	if (!Array.isArray(reservations)) return;

	commit('setReservations', {
		reservations: reservations.filter(
			(r) =>
				r.type?.toLowerCase() !== state.typeAccount.delivery &&
				r.type?.toLowerCase() !== state.typeAccount.takeaway,
		),
	});
	commit('setDeliveryReservations', {
		deliveryReservations: reservations.filter(
			(r) => r.type?.toLowerCase() === state.typeAccount.delivery,
		),
	});
	commit('setTakeawayReservations', {
		takeawayReservations: reservations.filter(
			(r) => r.type?.toLowerCase() === state.typeAccount.takeaway,
		),
	});
}

const state = {
	viewsCopilot: {
		kitchen: 'Cocina',
		bar: 'Barra',
		barManager: 'TPV',
		intern: 'Pasante',
	},
	typeAccount: {
		local: 'local',
		reservation: 'reservation',
		delivery: 'delivery',
		takeaway: 'takeaway',
	},

	notificationsType: {
		SECOND_DRINK: 'upselling_drinks_reminder',
	},

	selectedDate: DateTime.now().toISODate(),
	serverLogicalDate: null,
	latestManualDateOverride: null,
	// When true, user is viewing a date different from today and automatic date updates are blocked
	isViewingNonTodayDate: false,
	availableRoles: [],
	/** Full list of venue roles, preserved so we can restore it after staff logout. */
	allVenueRoles: [],
	selectedRoleId: '0',
	reservations: [],
	deliveryReservations: [],
	takeawayReservations: [],
	notifications: [],
	accounts: [],
	assistantNotifications: [],
	isPlayNotificationSound: false,
	isLoadingGetReservations: false,
	isLoadingPastReservations: false,
	isLoadingVenueMenus: false,
	isShowToastScanQr: null,
	isShowModalScanQr: null,
	toast: {
		showToast: false,
		toastMessage: '',
		idToast: '',
	},
	newNotification: null,
	commanderReservation: null,
	/** Id de la reserva del comandero; se usa para resolver la reserva actual desde state.reservations. */
	commanderReservationId: null,
	/** Si es true, al elegir cuenta en el overlay de selección de mesa (desde Commander sin reserva) se envía el pedido. */
	commanderSendOrderAfterSelectTable: false,
	/** Si es true, al abrir el overlay Commander se muestra la pestaña Pedidos (carrito) en lugar de Comandero. */
	commanderOpenWithOrdersView: false,
	notificationMode: '0',
	tables: [],
	reservationId: null,
	serviceEvents: [],
	selectedProductOrdersToServe: [],
	selectedProductOrdersToPrepared: [],
	/** TPV: cuando está activo, finishpreparation envía markServed para marcar bebidas como servidas fuera de barra (persiste en sessionStorage). */
	tpvMarkServedShiftMode: readInitialTpvMarkServedShiftMode(),
	selectedProductOrdersToClient: [],
	productOrder: null,
	selectedReservationId: null,

	isRemoveProduct: false,
	localProductIdModalReject: null,

	reservationUserUid: null,
	showAllProductOrdersInKitchen: true,
	/** Solo mostrar partidas que tengan al menos una de estas estaciones (vacío = sin filtro por inclusión). */
	selectedStations: [],
	/** Ocultar partidas que tengan alguna de estas estaciones (vacío = sin filtro por exclusión). */
	excludedStations: [],
	/**
	 * «Solo sin partidas»: el producto es visible si no tiene ids de partida, en unión con el «Solo» de
	 * partidas reales (selectedStations) si también hay alguna marcada.
	 */
	onlyProductOrdersWithoutElaborationStation: false,
	/**
	 * Solo con filtro «Ocultar» (excludedStations no vacío y sin «Solo»): si es false, los productos sin
	 * partida pasan a ghost. Por defecto true (se siguen viendo, comportamiento histórico).
	 */
	showProductOrdersWithoutElaborationWhenExcludingOnly: true,

	config: {
		zoneFilter: false,
	},
	selectedZoneFilter: [],
	selectedAreaFilter: readInitialSelectedAreaFilter(),

	promotionsEnabled: false,
	customerReadyToOrder: true,
	isNotificationCustomerReadyToOrder: {
		show: false,
		id: null,
	},

	typeServicesSearch: [],
	selectedProductOrderToModifyPrice: null,
	currentStaff: null,
	staffList: [],
	selectedRangeFilter: [],
	serviceRanges: [],
	serviceRangesInitialized: false,
	isKitchenShowReservationsAccount: false,
};

const getters = {
	selectedRangeFilter(state) {
		return state.selectedRangeFilter || [];
	},
	serviceRanges(state) {
		return (state.serviceRanges || []).filter((range) => range.enabled);
	},
	selectedRangeTableNames: (state) => {
		const selectedRangeIds = new Set(state.selectedRangeFilter.map(String));
		const tableNamesSet = new Set();

		state.serviceRanges.forEach((range) => {
			if (selectedRangeIds.has(String(range.id))) {
				if (Array.isArray(range.tables)) {
					range.tables.forEach((table) => {
						const name = table?.name ?? String(table);
						if (name) tableNamesSet.add(String(name).trim());
					});
				}
			}
		});

		return Array.from(tableNamesSet).sort((a, b) =>
			a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
		);
	},
	promotionsEnabled: (state) => {
		return state.promotionsEnabled;
	},
	customerReadyToOrder: (state) => {
		return state.customerReadyToOrder;
	},
	productOrder: (state) => {
		return state.productOrder;
	},
	isRemoveProduct: (state) => {
		return state.isRemoveProduct;
	},
	selectedReservationId: (state) => {
		return state.selectedReservationId;
	},
	selectedReservation: (state) => {
		return state.reservations.find((reservation) => reservation.id === state.selectedReservationId);
	},

	selectedProductOrdersToServe: (state) => {
		return state.selectedProductOrdersToServe;
	},
	selectedProductOrdersToPrepared: (state) => {
		return state.selectedProductOrdersToPrepared;
	},
	tpvMarkServedShiftMode: (state) => state.tpvMarkServedShiftMode,
	selectedProductOrderToModifyPrice: (state) => {
		return state.selectedProductOrderToModifyPrice;
	},
	selectedProductOrdersToClient: (state, getters) => {
		const readyProductOrdersIds =
			getters.serviceEventPendingService
				.flatMap((serviceEvent) => serviceEvent.items)
				?.filter(
					(productOrder) => productOrder.statusProductOrder < 100 /*&& productOrder.
				requirementProductCopilotRoles?.some((role) => role.name === getters.getSelectedRole?.
					name)*/,
				)
				?.map((productOrder) => productOrder.productOrderId) || [];

		return state.selectedProductOrdersToClient.filter((productOrderId) =>
			readyProductOrdersIds.includes(productOrderId),
		);
	},

	hotTable: (state) => {
		const STATUS_IN_HOT_TABLE = 7;
		let selectedRole = getters.getSelectedRole(state);
		const normalize = (reservations = []) =>
			reservations.flatMap((reservation) => {
				if (!reservation.cartSession || !reservation.cartSession.productOrders) return [];
				const isDelivery = reservation.type?.toLowerCase() === state.typeAccount.delivery;
				const isTakeaway = reservation.type?.toLowerCase() === state.typeAccount.takeaway;

				return reservation.cartSession.productOrders
					.filter((order) => order)
					.flatMap((order) => {
						return order.items
							.filter((item) => {
								const isStatus7 = item.statusProductOrder === STATUS_IN_HOT_TABLE;
								// Para delivery: excluir estado 7 de hottable (se queda en el listado principal)
								// Para no delivery: incluir estado 7 en hottable (comportamiento actual)
								const shouldExcludeFromHotTable = (isDelivery && isStatus7) || (isTakeaway && isStatus7);

								return (
									!productOrderService.checkIsGhostProductOrder(item) &&
									isStatus7 &&
									!shouldExcludeFromHotTable &&
									item.requirementProductCopilotRoles?.some((role) => role.name === selectedRole.copilotView.name)
								);
							})
							.map((item) => ({
								...item,
								createdDateTime: item.dateCreated || order.createdDateTime,
								reservationInfo: {
									zone: reservation.roomName,
									table: reservation.table,
									tables: reservation.tables,
									locationTag: reservation.locationTag,
									serviceMode: reservation.serviceMode,
									reservationId: reservation.id,
								},
							}));
					});
			});

		const all = [
			...normalize(state?.reservations),
			...normalize(state?.deliveryReservations),
			...normalize(state?.takeawayReservations),
		];

		all.sort((a, b) => new Date(a.createdDateTime) - new Date(b.createdDateTime));

		return all;
	},
	selectedDate: (state) => {
		return state.selectedDate;
	},
	latestManualDateOverride: (state) => {
		return state.latestManualDateOverride;
	},
	isViewingNonTodayDate: (state) => {
		return state.isViewingNonTodayDate;
	},
	serverLogicalDate: (state) => {
		return state.serverLogicalDate;
	},
	showTicketRequestNotifications: (state, getters) => {
		// Assuming isKitchenSelectedRole is also a getter
		return !getters.isKitchenSelectedRole; // "Esto no lo tengo tan seguro porque el del TPV imprime el ticket de una cuenta ->" || !selectedRoleObject?.name.includes("TPV + Barra");
	},
	getSelectedRole: (state) => {
		if (!state || !state.availableRoles || state.availableRoles.length === 0) {
			return null;
		}
		return state.availableRoles.find((role) => role.id === state.selectedRoleId);
	},
	isKitchenSelectedRole: (state) => {
		const kitchenRoleObject = state.availableRoles.find((role) => role.name === 'Cocina');
		const kitchenRole = kitchenRoleObject ? kitchenRoleObject.id : null;
		return state.selectedRoleId === kitchenRole;
	},
	isOnlyRoom: (state) => {
		const onlyRoomRoleObject = state.availableRoles.find((role) => role.name === '1 en Sala');
		const onlyRole = onlyRoomRoleObject ? onlyRoomRoleObject.id : null;
		return state.selectedRoleId === onlyRole;
	},
	isTpvPlusBarraSelectedRole(state, getters) {
		const role = getters.getSelectedRole;
		if (!role) {
			return false;
		}
		return isTpvPlusBarraRole(role.name) || isTpvPlusBarraRole(role.copilotView?.name);
	},
	canSelectElaborationStationsForDrinks(state, getters) {
		const role = getters.getSelectedRole;
		if (!role) {
			return false;
		}
		return (
			roleUsesDrinksPartidaFilter(role.name) || roleUsesDrinksPartidaFilter(role.copilotView?.name)
		);
	},
	pendingOrders: (state, getters) => {
		const selectedZones = Array.isArray(state.selectedZoneFilter)
			? state.selectedZoneFilter.filter((z) => typeof z === 'string')
			: [];

		const statusCartSessionRoleTypes = getters.isKitchenSelectedRole ? [2] : [1, 3];

		const orders = state.reservations
			.filter((reservation) => {
				if (isUnassignedReservation(reservation)) return true;
				const zone = reservation.roomName;
				return selectedZones.length === 0 || selectedZones.includes(zone);
			})
			.flatMap((reservation) => {
				if (!reservation.cartSession) return [];

				// Añadimos la zona a cada orden para que sea accesible después
				return reservation.cartSession.productOrders.map((order) => ({
					...order,
					reservationZone: reservation.roomName,
				}));
			})
			.filter((order) => order && statusCartSessionRoleTypes.includes(order.cartSessionStatus))
			.map((order) => {
				let items = order.items || [];
				if (order.cartSessionStatus > 1) {
					items = items.filter((item) => item.type === 'Comida');
				}
				items = items.filter((item) => productOrderService.isVisibleForElaborationStationFilter(item));
				return {
					...order,
					items,
				};
			})
			.filter((order) => order.items.length > 0)
			.sort((a, b) => new Date(a.createdDateTime) - new Date(b.createdDateTime));

		// Agrupa los items por SKU y choices (esto puede usar la zona si lo necesitas luego)
		const groupedOrders = orders.map((order) => ({
			...order,
			items: groupItemsBySkuAndChoices([order]),
		}));

		return groupedOrders;
	},

	allOrderProducts(state) {
		const normalize = (reservations = []) =>
			reservations
				.flatMap((r) => r?.cartSession?.productOrders ?? [])
				.filter(Boolean)
				.flatMap((order) =>
					(order.items ?? []).map((item) => ({
						...item,
						createdDateTime: item?.dateCreated ?? order?.createdDateTime ?? null,
					})),
				);

		const all = [
			...normalize(state?.reservations),
			...normalize(state?.deliveryReservations),
			...normalize(state?.takeawayReservations),
		];

		all.sort((a, b) => {
			const ta = a.createdDateTime ? Date.parse(a.createdDateTime) : Number.POSITIVE_INFINITY;
			const tb = b.createdDateTime ? Date.parse(b.createdDateTime) : Number.POSITIVE_INFINITY;
			return ta - tb;
		});

		return all;
	},

	availableRoles: (state) => {
		return state.availableRoles;
	},
	allVenueRoles: (state) => {
		return state.allVenueRoles;
	},
	selectedRole: (state) => {
		return state.selectedRoleId;
	},
	reservations: (state) => {
		return state.reservations.slice().sort((a, b) => {
			return new Date(a.startDateTime) - new Date(b.startDateTime);
		});
	},
	deliveryReservations: (state) => {
		if (state.deliveryReservations && state.deliveryReservations.length > 0) {
			return state.deliveryReservations.slice().sort((a, b) => {
				return new Date(a.startDateTime) - new Date(b.startDateTime);
			});
		}
		return [];
	},
	takeawayReservations: (state) => {
		if (state.takeawayReservations && state.takeawayReservations.length > 0) {
			return state.takeawayReservations.slice().sort((a, b) => {
				return new Date(a.startDateTime) - new Date(b.startDateTime);
			});
		}
		return [];
	},
	notifications: (state, getters) => {
		const deps = playableGateReactiveDeps(state, getters);
		return filterNotificationsByPlayableGate(state.notifications || [], {
			selectedRole: deps.selectedRole,
			selectedZones: deps.selectedZones,
			allowedTables: deps.allowedTables,
		});
	},
	accounts: (state) => {
		return state.accounts;
	},
	assistantNotifications: (state, getters) => {
		const deps = playableGateReactiveDeps(state, getters);
		return filterNotificationsByPlayableGate(state.assistantNotifications || [], {
			selectedRole: deps.selectedRole,
			selectedZones: deps.selectedZones,
			allowedTables: deps.allowedTables,
		});
	},

	isPlayNotificationSound: (state) => {
		return state.isPlayNotificationSound;
	},
	isLoadingGetReservations: (state) => {
		return state.isLoadingGetReservations;
	},
	isLoadingPastReservations: (state) => {
		return state.isLoadingPastReservations;
	},
	isLoadingVenueMenus: (state) => {
		return state.isLoadingVenueMenus;
	},
	isShowToastScanQr: (state) => {
		return state.isShowToastScanQr;
	},
	toast: (state) => {
		return state.toast;
	},
	newNotification: (state) => {
		return state.newNotification;
	},
	menusVenue: (state) => {
		return state.venueMenus;
	},
	/** Reserva actual del comandero: se resuelve desde reservations por id para que pax y demás cambios se reflejen. */
	commanderReservation: (state) => {
		const id = state.commanderReservationId;
		if (id == null) return state.commanderReservation;
		const fromList = state.reservations.find((r) => r.id === id || r.realId === id);
		return fromList != null ? fromList : state.commanderReservation;
	},
	getIsCommanderSendOrderAfterSelectTable: (state) => state.commanderSendOrderAfterSelectTable,
	getCommanderOpenWithOrdersView: (state) => state.commanderOpenWithOrdersView,
	notificationMode: (state) => {
		return state.notificationMode;
	},
	tables: (state) => {
		return state.tables;
	},
	barraIsLocated(state) {
		return isLocatedBarMode(state.tables);
	},
	reservationId(state) {
		return state.reservationId;
	},
	reservationUserUid(state) {
		return state.reservationUserUid;
	},
	serviceEvents: (state) => {
		return state.serviceEvents;
	},
	serviceEventPendingService(state, getters, rootState) {
		const selectedZones = Array.isArray(state.selectedZoneFilter)
			? state.selectedZoneFilter.filter((z) => typeof z === 'string')
			: [];

		const selectedRangeIds = Array.isArray(state.selectedRangeFilter) ? state.selectedRangeFilter.map(String) : [];
		const ranges = Array.isArray(state.serviceRanges) ? state.serviceRanges : [];

		let allowedTablesSet = null;

		if (selectedRangeIds.length > 0) {
			allowedTablesSet = new Set(
				ranges
					.filter((r) => selectedRangeIds.includes(String(r.id)))
					.flatMap((r) => (r.tables || []).map((t) => String(t?.name ?? t?.table ?? t?.number ?? t)))
					.filter(Boolean)
					.map((t) => t.trim()),
			);
		}
		// ------------------------------------------------------------------------

		const pending = state.serviceEvents
			.filter((serviceEvent) => serviceEvent.serviceEventStatus !== 100)
			.map((serviceEvent) => {
				const selectedRoleObject = state.availableRoles.find((role) => role.id === state.selectedRoleId);

				const servesDrinks =
					selectedRoleObject?.notificationTypes?.some((nt) => nt.name === 'order_ready_drinks') || false;
				const servesFood = selectedRoleObject?.notificationTypes?.some((nt) => nt.name === 'order_ready_food') || false;

				const itemsFiltered = serviceEvent.items.filter((item) => {
					const roleName = selectedRoleObject?.copilotView?.name;
					const isElaborationRole = roleName?.includes('Cocina') || roleName?.includes('Barra');
					const isRoleMatch = isElaborationRole
						? item.requirementProductCopilotRoles.some((role) => roleName.includes(role.name))
						: item.requirementProductCopilotRoles.some(
								(role) => (servesFood && role.name === 'Cocina') || (servesDrinks && role.name === 'Barra'),
							);

					const itemZone = item.reservationInfo?.zone;
					const matchZone = selectedZones.includes(itemZone);

					const isStatusValid = item.statusProductOrder !== 100;

					const itemTable = String(item.reservationInfo?.table ?? '').trim();
					const matchRange = allowedTablesSet?.has(itemTable);

					const isFilterMatch = (selectedZones.length === 0 && !allowedTablesSet) || matchZone || matchRange;

					const matchesPartidaFilter = productOrderService.isVisibleForElaborationStationFilter(item);

					return isRoleMatch && isStatusValid && isFilterMatch && matchesPartidaFilter;
				});

				return {
					...serviceEvent,
					items: itemsFiltered,
				};
			})
			.filter((serviceEvent) => serviceEvent.items.length > 0);

		const claimEnabled = rootState?.venue?.copilotConfig?.copilotServiceEventClaim === true;
		if (!claimEnabled) {
			return pending;
		}

		const myStaffId = state.currentStaff?.id != null ? String(state.currentStaff.id) : null;

		return [...pending].sort((a, b) => {
			const aClaimedByOther = Boolean(a.tendingStaffId) && String(a.tendingStaffId) !== myStaffId;
			const bClaimedByOther = Boolean(b.tendingStaffId) && String(b.tendingStaffId) !== myStaffId;
			if (aClaimedByOther === bClaimedByOther) return 0;
			return aClaimedByOther ? 1 : -1;
		});
	},

	view(state) {
		const role = state.availableRoles.find((role) => role.id === state.selectedRoleId);
		hideAllOffcanvas();
		return role && role.copilotView && role.copilotView.name ? role.copilotView.name : null;
	},
	copilotViewRolName(state) {
		const role = state.availableRoles.find((role) => role.id === state.selectedRoleId);
		return role && role.copilotView && role.name ? role.name : role.copilotView.name;
	},
	elaborationStations(state, getters) {
		const role = getters.getSelectedRole;
		if (role?.elaborationStations?.length) {
			return role.elaborationStations;
		}

		// TPV / TPV+Barra / 1 en Sala borrow only Barra partidas (never Cocina union).
		if (getters.canSelectElaborationStationsForDrinks) {
			const roles = state.allVenueRoles.length ? state.allVenueRoles : state.availableRoles;
			return collectBarraElaborationStations(roles);
		}

		return [];
	},
	reservationsWithComments(state) {
		return state.reservations.map((reservation) => {
			const cartSessionOrders = (reservation.cartSession?.productOrders || []).map((cartSessionOrder) => {
				const items = cartSessionOrder.items.map((item) => item.name).join(', ');
				const isAllProductsServed = cartSessionOrder.items.every((item) => item.statusProductOrder === 100);
				const createdDateTime = cartSessionOrder.createdDateTime;

				// Recoger comentarios de los productos y el mensaje general del pedido
				// Solo incluimos comentarios de productos que son "Menú" en la cabecera para evitar duplicados en productos simples
				const itemComments = (cartSessionOrder.items || [])
					.filter((item) => item.staffComments && (item.isMenu === 'SI' || item.isMenu === true))
					.map((item) => {
						if (Array.isArray(item.staffComments)) {
							return item.staffComments.map((c) => (typeof c === 'string' ? c : c.comment)).join(', ');
						}
						return item.staffComments;
					});

				const allMessages = [cartSessionOrder.clientMessage, ...itemComments].filter(
					(message) => message && typeof message === 'string' && message.trim() !== '',
				);

				const clientMessage = allMessages.length ? [...new Set(allMessages)].join(', ') : null;

				return { createdDateTime, isAllProductsServed, items, clientMessage };
			});
			return { reservationId: reservation.id, table: reservation.table, cartSessionOrders };
		});
	},
	localProductIdModalReject: (state) => {
		return state.localProductIdModalReject;
	},
	showAllProductOrdersInKitchen(state) {
		return state.showAllProductOrdersInKitchen;
	},
	selectedStations(state) {
		return state.selectedStations;
	},
	excludedStations(state) {
		return state.excludedStations;
	},
	onlyProductOrdersWithoutElaborationStation(state) {
		return Boolean(state.onlyProductOrdersWithoutElaborationStation);
	},
	showProductOrdersWithoutElaborationWhenExcludingOnly(state) {
		return Boolean(state.showProductOrdersWithoutElaborationWhenExcludingOnly);
	},
	/** Filtro por estación activo: no "todas" y hay criterio en partidas o solo «Ocultar sin partidas». */
	isElaborationStationFilterActive(state) {
		if (state.onlyProductOrdersWithoutElaborationStation) {
			return !state.showAllProductOrdersInKitchen;
		}
		const hasInclude = (state.selectedStations?.length ?? 0) > 0;
		const hasExclude = (state.excludedStations?.length ?? 0) > 0;
		const hideSinPartidaSolo =
			!hasInclude && !hasExclude && state.showProductOrdersWithoutElaborationWhenExcludingOnly === false;
		return !state.showAllProductOrdersInKitchen && (hasInclude || hasExclude || hideSinPartidaSolo);
	},

	zoneReservation(state) {
		const zones = [...new Set(state.tables.map((table) => table.room))];
		return appendUnassignedZoneIfNeeded(zones, state.reservations);
	},

	zoneFilter(state) {
		return state.config?.zoneFilter;
	},
	selectedZoneFilter(state) {
		return state.selectedZoneFilter;
	},
	selectedAreaFilter(state) {
		return state.selectedAreaFilter || 'hour';
	},

	pendingProductsByRole(state, getters) {
		const selectedRole = getters.getSelectedRole;
		if (!selectedRole) return [];

		const targetRoleName = selectedRole.copilotView?.name;
		if (!targetRoleName) return [];

		// Función auxiliar para crear nombres descriptivos con opciones
		const createDisplayName = (productName, choices) => {
			if (!choices || choices.length === 0) {
				return productName;
			}

			// Crear un nombre descriptivo que incluya las opciones importantes
			const optionsText = choices
				.map((choice) => {
					// Usar las propiedades correctas según la estructura real
					return choice.choiceName;
				})
				.join(', ');

			return `${productName} (${optionsText})`;
		};

		const zoneRangeActive = isKitchenZoneRangeFilterActive(
			state.selectedZoneFilter || [],
			state.selectedRangeFilter || [],
		);

		// Misma ventana de reservas que el listado principal (zona / rango de mesas)
		const reservationsForBadges = (state.reservations || [])
			.filter((reservation) => reservation.cartSession && reservation.cartSession.productOrders)
			.filter(
				(reservation) =>
					!zoneRangeActive ||
					reservationPassesKitchenZoneRangeFilter(reservation, {
						selectedZoneFilter: Array.isArray(state.selectedZoneFilter) ? state.selectedZoneFilter : [],
						rangeTableNames: getters.selectedRangeTableNames || [],
					}),
			);

		// Obtener todos los productos pendientes de esas reservas
		const allPendingProducts = reservationsForBadges.flatMap((reservation) =>
			reservation.cartSession.productOrders
				.filter((order) => order.items && order.items.length > 0)
				.flatMap((order) =>
					order.items
						.filter(
							(item) =>
								item.statusProductOrder < 100 &&
								item.statusProductOrder > 0 &&
								item.statusProductOrder !== 3 &&
								item.requirementProductCopilotRoles?.some((role) => role.name === targetRoleName) &&
								!productOrderService.checkIsGhostProductOrder(item),
						)
						.map((item) => ({
							...item,
							reservationId: reservation.id,
							table: reservation.table,
							roomName: reservation.roomName,
						})),
				),
		);

		// Agrupar productos por nombre y opciones
		const groupedProducts = {};

		allPendingProducts.forEach((product) => {
			let choices = product.choices || product.options || product.productOptions || product.modifiers || [];

			// Crear clave única que incluya SKU y opciones específicas
			let choicesKey = 'sin-opciones';

			if (choices && choices.length > 0) {
				// Ordenar las opciones para asegurar consistencia en la agrupación
				const sortedChoices = choices
					.map((choice) => {
						// Usar las propiedades correctas según la estructura real
						const name = choice.typeName || choice.choiceName || choice.type || choice.name || 'opcion';
						const value = choice.choice || choice.choiceName || choice.value || choice.text || 'valor';
						return `${name}:${value}`;
					})
					.sort();
				choicesKey = sortedChoices.join('|');
			}

			// Formatos de venta distintos del mismo producto (p. ej. "Unidad" vs "Ración") nunca deben
			// fusionarse en un mismo contador, igual que en la vista de cocina/barra.
			const sellingFormatKey = product.sellingFormatId ?? 'no-format';
			const productKey = `${product.productSku}_${sellingFormatKey}_${choicesKey}`;

			if (!groupedProducts[productKey]) {
				groupedProducts[productKey] = {
					productName: product.name,
					productSku: product.productSku,
					sellingFormatId: product.sellingFormatId ?? null,
					sellingFormatName: product.sellingFormatName ?? null,
					sellingFormatShortName: product.sellingFormatShortName ?? null,
					sellingFormatColor: product.sellingFormatColor ?? null,
					choices: choices,
					totalUnits: 0,
					reservations: [],
					statusCounts: {},
					displayName: createDisplayName(product.name, choices),
					dishClassIds: new Set(),
				};
			}

			const dishClassId = product.dishClass?.id;
			if (dishClassId != null) {
				groupedProducts[productKey].dishClassIds.add(dishClassId);
			}

			// Sumar unidades
			const units = product.units || 1;
			groupedProducts[productKey].totalUnits += units;

			// Contar por estado
			const status = product.statusProductOrder;
			if (!groupedProducts[productKey].statusCounts[status]) {
				groupedProducts[productKey].statusCounts[status] = 0;
			}
			groupedProducts[productKey].statusCounts[status] += units;

			// Agregar información de reserva
			groupedProducts[productKey].reservations.push({
				reservationId: product.reservationId,
				table: product.table,
				roomName: product.roomName,
				units: units,
				status: status,
			});
		});

		return Object.values(groupedProducts)
			.sort((a, b) => b.totalUnits - a.totalUnits)
			.map((product) => {
				const { dishClassIds, ...rest } = product;
				const dishClassIdsList = dishClassIds instanceof Set ? [...dishClassIds] : [...(dishClassIds || [])];
				return {
					...rest,
					dishClassIds: dishClassIdsList,
					displayUnits: `${product.totalUnits}`,
					displayText: `${product.displayName}`,
					hasUrgentStatus: product.statusCounts[2] > 0,
					statusSummary: Object.entries(product.statusCounts)
						.map(([status, count]) => `${count} (estado ${status})`)
						.join(', '),
				};
			});
	},

	reservationsWithProducts: (state, getters) => {
		const normalize = (reservations = []) =>
			reservations
				.filter((reservation) => reservation.status > 0)
				.map((reservation) => ({
					...reservation,
					orderedItems: reservation.cartSession
						? reservation.cartSession.productOrders.flatMap(
								(order) =>
									order.items.map((item) => ({
										...item,
										createdDateTime: item.dateCreated || order.createdDateTime,
										table: reservation.table ?? null,
									})),
								// eslint-disable-next-line no-mixed-spaces-and-tabs
							)
						: [],
				}));

		const all = [
			...normalize(state?.reservations),
			...(getters.showDelivery ? normalize(state?.deliveryReservations) : []),
			...(getters.showTakeaway ? normalize(state?.takeawayReservations) : []),
		];
		all.sort((a, b) => {
			const minDateA = a.orderedItems
				.filter((item) => item.statusProductOrder < 100)
				.map((item) => new Date(item.createdDateTime.replace(' ', 'T')))
				.reduce((min, current) => (current < min ? current : min), new Date(8640000000000000)); // Máxima fecha posible

			const minDateB = b.orderedItems
				.filter((item) => item.statusProductOrder < 100)
				.map((item) => new Date(item.createdDateTime.replace(' ', 'T')))
				.reduce((min, current) => (current < min ? current : min), new Date(8640000000000000)); // Máxima fecha posible

			return minDateA - minDateB;
		});

		return all;
	},

	baseReservationsWithCartAndProducts: (state, getters, rootState, rootGetters) => {
		const externalIsGhost = (item) => productOrderService.checkIsGhostProductOrder(item);

		const showOtherKitchenAccounts = rootGetters['preparationArea/showOtherKitchenAccounts'] ?? true;

		const areAllProductOrdersCompleted = (reservation) => {
			if (!reservation.cartSession?.productOrders) return false;
			return reservation.cartSession.productOrders.every((order) => {
				// const orderDone = order.cartSessionStatus === 2 || order.cartSessionStatus === 100
				const items = order.items || [];
				const hasItems = items.length > 0;
				const itemsDone =
					hasItems &&
					items.every(
						(it) =>
							it.statusProductOrder === 100 ||
							(it.statusProductOrder >= 3 && it.statusProductOrder < 7) ||
							it.statusProductOrder <= -1 ||
							it.statusProductOrder === -3,
					);

				return /*orderDone && */ itemsDone;
			});
		};

		// Get the current role view name for delivery status filtering
		const selectedRole = rootGetters['copilot/getSelectedRole'];
		const currentViewName = (selectedRole?.copilotView?.name || '').toLowerCase();

		return getters.reservationsWithProducts.filter((reservation) => {
			let menuEnabled;

			switch (reservation.type.toLowerCase()) {
				case state.typeAccount.local:
					menuEnabled = reservation.menuAccessEnabled === 1;
					break;
				case state.typeAccount.delivery: {
					// Base check: reservation must be active
					const isActive = reservation.status >= 0 && reservation.status !== 10;
					if (!isActive) return false;

					// Filter delivery reservations by deliveryStatus based on current role view
					const deliveryStatus = reservation.deliveryInfo?.deliveryStatus;

					if (currentViewName === 'cocina') {
						// Cocina sees delivery orders in PREPARING status
						return deliveryStatus === DELIVERY_STATUS.PREPARING || deliveryStatus === null;
					} else if (currentViewName === 'barra') {
						// Barra sees delivery orders in REVIEW status
						return deliveryStatus === DELIVERY_STATUS.REVIEW;
					}

					// Other views (Pasante, etc.): show all active delivery orders
					return true;
				}
				case state.typeAccount.takeaway: {
					// Base check: reservation must be active
					const isActive = reservation.status >= 0 && reservation.status !== 10;
					if (!isActive) return false;

					const takeawayStatus = reservation.takeAwayInfo?.preparationStatus;

					if (currentViewName === 'cocina') {
						return takeawayStatus === TAKEAWAY_STATUS.PREPARING || takeawayStatus === null;
					} else if (currentViewName === 'barra') {
						return takeawayStatus === TAKEAWAY_STATUS.REVIEW;
					}

					// Other views (Pasante, etc.): show all active takeaway orders
					return true;
				}
			}
			const hasCart = !!reservation.cartSession;
			const notCompleted = !areAllProductOrdersCompleted(reservation);
			if (!menuEnabled || !hasCart || !notCompleted) return false;

			/* Solo «Ver otras cuentas»: desmarcado = ocultar cuentas sin ningún producto no-fantasma.
			 * Independiente de «Ver otras partidas» (líneas fantasma en el listado de platos). */
			if (!showOtherKitchenAccounts) {
				const allProducts = reservation.cartSession.productOrders.flatMap((po) => po.items || []);
				const hasVisibleProducts = allProducts.some((p) => !externalIsGhost(p));
				if (!hasVisibleProducts) return false;
			}

			return true;
		});
	},

	onlyBeveragesFromDishClass: (state, getters, rootState, rootGetters) => {
		const selectedRole = rootGetters['copilot/getSelectedRole'];
		const myRoleName = selectedRole?.copilotView?.name;
		if (!myRoleName) return [];

		const target = myRoleName.toLowerCase();

		const hasItemForMyRole = (item) =>
			(item?.requirementProductCopilotRoles || []).some((role) => (role?.name || '').toLowerCase() === target);

		return (getters.baseReservationsWithCartAndProducts || []).filter((reservation) => {
			const items = reservation?.cartSession?.productOrders
				? reservation.cartSession.productOrders.flatMap((po) => po.items || [])
				: [];

			if (items.length === 0) return false;
			return !items.some(hasItemForMyRole);
		});
	},

	amountPaxOnlyBeveragesFromDishClass: (state, getters) => {
		return getters.onlyBeveragesFromDishClass.reduce((t, r) => t + (r.pax || 0), 0);
	},

	/** Cuentas con solo productos que no pasan por el rol actual (p. ej. solo bebida en cocina), en sala y activas. */
	activeOnlyBeveragesFromDishClass: (state, getters) => {
		return (getters.onlyBeveragesFromDishClass || []).filter(
			(r) => r && r.startDateTime && r.menuAccessEnabled === 1 && !r.finishedDateTime && r.status !== 10,
		);
	},

	amountPaxActiveOnlyBeveragesFromDishClass: (state, getters) => {
		return (getters.activeOnlyBeveragesFromDishClass || []).reduce((t, r) => t + (r.pax || 0), 0);
	},

	sortedReservationsNotCartSession: (state, getters) => {
		return (getters.reservationsWithProducts || []).filter(
			(reservation) => reservation.status !== 10 && reservation.menuAccessEnabled === 1 && !reservation.cartSession,
		);
	},

	amountPaxReservationsNotCartSession: (state, getters) => {
		return (getters.sortedReservationsNotCartSession || []).reduce((total, r) => total + (r.pax || 0), 0);
	},

	sortedReservations: (state, getters) => {
		const areAllProductOrdersCompleted = (reservation) => {
			if (!reservation.cartSession?.productOrders) return false;

			return reservation.cartSession.productOrders.every((order) => {
				const orderDone = order.cartSessionStatus === 2; /*|| order.cartSessionStatus === 100*/
				const items = order.items || [];
				const hasItems = items.length > 0;
				const itemsDone =
					hasItems &&
					items.every(
						(it) => it.statusProductOrder === 100 || it.statusProductOrder >= 3 || it.statusProductOrder <= -1,
					);
				return orderDone && itemsDone;
			});
		};

		return (getters.reservationsWithProducts || []).filter(
			(reservation) => reservation.menuAccessEnabled !== 1 && !areAllProductOrdersCompleted(reservation),
		);
	},
	reservationsToCome: (state, getters) => {
		return (getters.reservations || []).filter(
			(reservation) => !reservation.finishedDateTime && reservation.menuAccessEnabled === 0 && reservation.status == 1,
		);
	},

	amountPaxSortedReservations: (state, getters) => {
		return (getters.sortedReservations || []).reduce((total, r) => total + (r.pax || 0), 0);
	},

	finalizedReservations: (state, getters) => {
		return (getters.reservationsWithProducts || []).filter((reservation) => !!reservation.finishedDateTime);
	},

	amountPaxFinalizedReservations: (state, getters) => {
		return (getters.finalizedReservations || []).reduce((total, r) => total + (r.pax || 0), 0);
	},

	countFinalizedReservations: (state, getters) => {
		return (getters.finalizedReservations || []).length;
	},

	amountPaxTotalReservations: (state, getters) => {
		return (getters.reservationsToCome || []).reduce((total, r) => total + (r.pax || 0), 0);
	},
	countTotalReservations: (state, getters) => {
		return getters.reservationsToCome || [];
	},
	isNotificationCustomerReadyToOrder: (state) => {
		return state.isNotificationCustomerReadyToOrder;
	},

	accountsActive: (state, getters, rootState, rootGetters) => {
		const showBar = rootGetters['venue/copilotConfig'];
		const view = rootGetters['copilot/view'];
		const selectedZones = getters.zoneFilter
			? Array.isArray(state.selectedZoneFilter)
				? state.selectedZoneFilter.filter((z) => typeof z === 'string')
				: []
			: [];
		const allowedTablesArray = getters.selectedRangeTableNames;
		const allowedTables = new Set(allowedTablesArray);

		let list = state.reservations.filter(
			(reservation) =>
				reservation.menuAccessEnabled === 1 &&
				reservation.status > 0 &&
				!reservation.finishedDateTime &&
				reservation.status !== 10,
		);

		if (selectedZones.length > 0 || allowedTables.size > 0) {
			list = list.filter((reservation) =>
				reservationMatchesZoneOrRangeFilter(reservation, selectedZones, allowedTables),
			);
		}

		if (view !== 'TPV') {
			if (!showBar.weCopilotShowBar) {
				list = list.filter((r) => r.roomName?.toLowerCase() !== 'barra');
			}
		}

		return list.sort((a, b) => {
			const isAAccountPayment = a.status === 7;
			const isBAccountPayment = b.status === 7;

			const isAStarted = a.menuAccessEnabled === 1;
			const isBStarted = b.menuAccessEnabled === 1;

			if (isAAccountPayment && !isBAccountPayment) return -1;
			if (!isAAccountPayment && isBAccountPayment) return 1;

			if (isAStarted && !isBStarted) return -1;
			if (!isAStarted && isBStarted) return 1;

			return 0;
		});
	},
	accountsFinished: (state, getters, rootState, rootGetters) => {
		const showBar = rootGetters['venue/copilotConfig'];
		const view = rootGetters['copilot/view'];
		const selectedZones = getters.zoneFilter
			? Array.isArray(state.selectedZoneFilter)
				? state.selectedZoneFilter.filter((z) => typeof z === 'string')
				: []
			: [];
		const allowedTables = getters.selectedRangeTableNames;

		let list = state.reservations.filter((r) => r.menuAccessEnabled === 1 && !!parseFinishedDateTime(r.finishedDateTime));

		if (selectedZones.length > 0 || allowedTables.size > 0) {
			list = list.filter((reservation) =>
				reservationMatchesZoneOrRangeFilter(reservation, selectedZones, allowedTables),
			);
		}

		if (view !== 'TPV' && !showBar.weCopilotShowBar) {
			list = list.filter((r) => r.roomName?.toLowerCase() !== 'barra');
		}

		return list.sort((a, b) => {
			const dateA = new Date(parseFinishedDateTime(a.finishedDateTime));
			const dateB = new Date(parseFinishedDateTime(b.finishedDateTime));

			return dateB - dateA;
		});
	},

	accountsNoShow: (state, getters, rootState, rootGetters) => {
		const showBar = rootGetters['venue/copilotConfig'];
		const view = rootGetters['copilot/view'];
		const selectedZones = getters.zoneFilter
			? Array.isArray(state.selectedZoneFilter)
				? state.selectedZoneFilter.filter((z) => typeof z === 'string')
				: []
			: [];
		const allowedTables = getters.selectedRangeTableNames;

		let list = state.reservations.filter((reservation) => reservation.status === -4);

		if (selectedZones.length > 0 || allowedTables.size > 0) {
			list = list.filter((reservation) =>
				reservationMatchesZoneOrRangeFilter(reservation, selectedZones, allowedTables),
			);
		}

		if (view !== 'TPV') {
			if (!showBar.weCopilotShowBar) {
				list = list.filter((r) => r.roomName?.toLowerCase() !== 'barra');
			}
		}

		return list.sort((a, b) => {
			const isAAccountPayment = a.status === 7;
			const isBAccountPayment = b.status === 7;

			const isAStarted = a.menuAccessEnabled === 1;
			const isBStarted = b.menuAccessEnabled === 1;

			if (isAAccountPayment && !isBAccountPayment) return -1;
			if (!isAAccountPayment && isBAccountPayment) return 1;

			if (isAStarted && !isBStarted) return -1;
			if (!isAStarted && isBStarted) return 1;

			return 0;
		});
	},

	accountsReservation: (state, getters, rootState, rootGetters) => {
		const showBar = rootGetters['venue/copilotConfig'];
		const view = rootGetters['copilot/view'];
		const selectedZones = getters.zoneFilter
			? Array.isArray(state.selectedZoneFilter)
				? state.selectedZoneFilter.filter((z) => typeof z === 'string')
				: []
			: [];
		const allowedTables = getters.selectedRangeTableNames;

		let list = state.reservations.filter(
			(reservation) =>
				reservation.menuAccessEnabled === 0 &&
				reservation.status > 0 &&
				!reservation.finishedDateTime &&
				reservation.status !== 10,
		);

		const isFilterActive = selectedZones.length > 0 || allowedTables.size > 0;

		list = list.map((reservation) => {
			let matchFilter = true;
			if (isFilterActive) {
				matchFilter = reservationMatchesZoneOrRangeFilter(
					reservation,
					selectedZones,
					allowedTables,
				);
			}

			return {
				...reservation,
				matchFilter: matchFilter,
			};
		});

		if (view !== 'TPV') {
			if (!showBar.weCopilotShowBar) {
				list = list.filter((r) => r.roomName?.toLowerCase() !== 'barra');
			}
		}

		return list.sort((a, b) => {
			const isAAccountPayment = a.status === 7;
			const isBAccountPayment = b.status === 7;

			const isAStarted = a.menuAccessEnabled === 1;
			const isBStarted = b.menuAccessEnabled === 1;

			if (isAAccountPayment && !isBAccountPayment) return -1;
			if (!isAAccountPayment && isBAccountPayment) return 1;

			if (isAStarted && !isBStarted) return -1;
			if (!isAStarted && isBStarted) return 1;

			return 0;
		});
	},

	typeServicesSearch: (state) => {
		return state.typeServicesSearch;
	},
	currentStaff: (state) => {
		return state.currentStaff;
	},
	staffList: (state) => {
		return state.staffList;
	},

	showDelivery: () => {
		return true;
		/*const posProvider = rootGetters["venue/posProvider"];*/
		//const user = rootGetters["user/user"];
		//return posProvider === POS_PROVIDERS.LAST_APP && user?.email === "admin@wewelcom.io";
	},
	showTakeaway: () => {
		return true;
	},

	isKitchenShowReservationsAccount: (state) => {
		return state.isKitchenShowReservationsAccount;
	},
};

const mutations = {
	setSelectedRangeFilter(state, payload) {
		if (payload === null || payload === undefined) {
			state.selectedRangeFilter = [];
			localStorage.setItem('selectedRangeFilter', JSON.stringify([]));
			return;
		}

		const ids = Array.isArray(payload) ? payload.filter((id) => id !== null && id !== undefined).map(String) : [];

		state.selectedRangeFilter = ids;
		localStorage.setItem('selectedRangeFilter', JSON.stringify(ids));
	},
	setServiceRanges(state, payload) {
		const ranges = Array.isArray(payload) ? payload : payload?.serviceRanges || [];
		state.serviceRanges = ranges;
		state.serviceRangesInitialized = true;
	},
	setProductOrder(state, payload) {
		state.productOrder = payload.productOrder;
	},
	setSelectedReservationId(state, payload) {
		state.selectedReservationId = payload.reservationId;
	},
	setSelectedReservationUserId(state, payload) {
		state.reservationUserUid = payload;
	},
	setIsRemoveProduct(state, value) {
		state.isRemoveProduct = value;
	},
	setTypeServicesSearch(state, value) {
		state.typeServicesSearch = value;
	},
	setSelectedProductOrderToModifyPrice(state, value) {
		state.selectedProductOrderToModifyPrice = value;
	},
	setSelectedDate(state, payload) {
		const payloadDate = payload.selectedDate;
		const fallbackDate = state.serverLogicalDate || DateTime.now().toISODate();
		let newDate;
		if (typeof payloadDate === 'string') {
			// Reject stringified null/undefined from bad callers (e.g. pickadate null + '')
			if (payloadDate === 'null' || payloadDate === 'undefined' || payloadDate.trim() === '') {
				newDate = null;
			} else {
				newDate = DateTime.fromISO(payloadDate).toISODate();
			}
		} else if (payloadDate instanceof Date) {
			newDate = DateTime.fromJSDate(payloadDate).toISODate();
		} else {
			newDate = null;
		}
		// Never persist an invalid date — fall back to server logical date / today
		state.selectedDate = newDate || fallbackDate;

		const referenceDate = fallbackDate;
		const normalizedReference = String(referenceDate).slice(0, 10);
		const normalizedNew = String(state.selectedDate).slice(0, 10);

		state.isViewingNonTodayDate = normalizedNew !== normalizedReference;
	},
	setLatestManualDateOverride(state, payload) {
		state.latestManualDateOverride = payload.timestamp;
	},
	setIsViewingNonTodayDate(state, payload) {
		state.isViewingNonTodayDate = payload;
	},
	setServerLogicalDate(state, date) {
		state.serverLogicalDate = date;
	},
	setAvailableRoles(state, payload) {
		state.availableRoles = payload.availableRoles;
		if (payload.isVenueMaster) {
			state.allVenueRoles = payload.availableRoles;
		}
	},
	setSelectedRoleId(state, payload) {
		let finalId = payload.selectedRoleId;
		const roleExists = state.availableRoles.some((r) => r.id === finalId);

		if (!roleExists) {
			const sala = state.availableRoles.find((r) => r.name === 'Sala');
			finalId = sala ? sala.id : null;
		}

		if (finalId) {
			cookieManager.setCookie('selectedRole', finalId);
		}

		state.selectedRoleId = finalId;

		if (finalId != null && finalId !== '') {
			const snapshot = loadCopilotStationFilterSnapshot(finalId);
			if (snapshot) {
				applyStationFilterSnapshotToCopilotState(state, snapshot);
			} else {
				applyStationFilterSnapshotToCopilotState(state, defaultStationFilterSnapshot());
			}
		} else {
			applyStationFilterSnapshotToCopilotState(state, defaultStationFilterSnapshot());
		}

		state.selectedReservationId = null;
	},
	setSelectedLocale(state, payload) {
		cookieManager.setCookie('selectedLocale', payload.selectedLocale);
		state.selectedLocale = payload.selectedLocale;
	},
	setReservations(state, payload) {
		state.reservations = enrichReservationsServiceMode(payload.reservations, state.tables);
		state.isLoadingGetReservations = false;
	},
	setDeliveryReservations(state, payload) {
		state.deliveryReservations = payload.deliveryReservations;
		state.isLoadingGetReservations = false;
	},
	setTakeawayReservations(state, payload) {
		state.takeawayReservations = payload.takeawayReservations;
		state.isLoadingGetReservations = false;
	},
	setServiceEvents(state, payload) {
		state.serviceEvents = normalizeServiceEvents(payload.serviceEvents);
	},
	setNotifications(state, payload) {
		if (state.notifications.length === 0 && payload.notifications.length > 0) {
			state.isPlayNotificationSound = true;
		}
		state.notifications = payload.notifications;
	},
	setIsPlayNotificationSound(state, payload) {
		state.isPlayNotificationSound = payload.isPlayNotificationSound;
	},
	setIsLoadingGetReservations(state, payload) {
		state.isLoadingGetReservations = payload.isLoadingGetReservations;
	},
	setIsLoadingPastReservations(state, payload) {
		state.isLoadingPastReservations = payload.isLoadingPastReservations;
	},
	mergePastReservations(state, payload) {
		const existingIds = new Set(state.reservations.map(r => r.id));
		const newReservations = payload.reservations.filter(r => !existingIds.has(r.id));
		state.reservations = [...state.reservations, ...newReservations];
		state.isLoadingPastReservations = false;
	},
	mergePastDeliveryReservations(state, payload) {
		const existingIds = new Set(state.deliveryReservations.map(r => r.id));
		const newReservations = payload.reservations.filter(r => !existingIds.has(r.id));
		state.deliveryReservations = [...state.deliveryReservations, ...newReservations];
	},
	mergePastTakeawayReservations(state, payload) {
		const existingIds = new Set(state.takeawayReservations.map(r => r.id));
		const newReservations = payload.reservations.filter(r => !existingIds.has(r.id));
		state.takeawayReservations = [...state.takeawayReservations, ...newReservations];
	},
	setIsLoadingVenueMenus(state, payload) {
		state.isLoadingVenueMenus = payload.isLoadingVenueMenus;
	},
	setAccounts(state, payload) {
		if (state.accounts.length === 0 && payload.accounts.length > 0) {
			state.isPlayNotificationSound = true;
		}
		state.accounts = payload.accounts;
	},
	setAssistantNotifications(state, payload) {
		state.assistantNotifications = payload.assistantNotifications;
	},
	setIsShowToastScanQr(state, payload) {
		state.isShowToastScanQr = payload.isShowToastScanQr;
	},
	setToast(state, payload) {
		state.toast = payload.toast;
	},
	setNewNotification(state, payload) {
		state.isPlayNotificationSound = true;
		state.newNotification = payload.newNotification;
	},
	setVenueMenus(state, payload) {
		state.venueMenus = payload.venueMenus;
	},
	setCommanderReservation(state, payload) {
		const r = payload.commanderReservation;
		state.commanderReservation = r;
		state.commanderReservationId = r != null ? (r.id ?? r.realId ?? null) : null;
	},
	setCommanderSendOrderAfterSelectTable(state, payload) {
		state.commanderSendOrderAfterSelectTable = !!payload;
	},
	setCommanderOpenWithOrdersView(state, payload) {
		state.commanderOpenWithOrdersView = !!payload;
	},
	setNotificationMode(state, payload) {
		state.notificationMode = payload.notificationMode;
	},
	setTables(state, payload) {
		state.tables = payload.tables;
	},
	setReservationId(state, id) {
		state.reservationId = id;
	},
	setSelectedProductOrdersToServe(state, payload) {
		state.selectedProductOrdersToServe = payload.selectedProductOrdersToServe;
	},
	setSelectedProductOrdersToPrepared(state, payload) {
		state.selectedProductOrdersToPrepared = payload.selectedProductOrdersToPrepared;
	},
	setTpvMarkServedShiftMode(state, value) {
		state.tpvMarkServedShiftMode = Boolean(value);
		try {
			if (state.tpvMarkServedShiftMode) {
				sessionStorage.setItem(COPILOT_TPV_MARK_SERVED_SESSION_KEY, '1');
			} else {
				sessionStorage.removeItem(COPILOT_TPV_MARK_SERVED_SESSION_KEY);
			}
		} catch {
			/* ignore */
		}
	},
	setSelectedProductOrdersToClient(state, payload) {
		state.selectedProductOrdersToClient = payload.selectedProductOrdersToClient();
	},
	updateProductOrdersStatus(state, payload) {
		const updateReservation = (reservation) => {
			if (!reservation.cartSession || !Array.isArray(reservation.cartSession.productOrders)) {
				return reservation;
			}
			return {
				...reservation,
				cartSession: {
					...reservation.cartSession,
					productOrders: reservation.cartSession.productOrders.map((productOrder) => {
						if (!Array.isArray(productOrder.items)) {
							return productOrder;
						}
						return {
							...productOrder,
							items: productOrder.items.map((item) => {
								if (payload.productOrdersIdsToUpdate.includes(item.productOrderId)) {
									const updatedItem = { ...item, statusProductOrder: payload.status };
									if (payload.status === 2 && !item.datePreparing) {
										updatedItem.datePreparing = { date: new Date().toISOString() };
									}
									return updatedItem;
								}
								return item;
							}),
						};
					}),
				},
			};
		};

		state.reservations = state.reservations.map(updateReservation);
		state.deliveryReservations = state.deliveryReservations.map(updateReservation);
		state.takeawayReservations = state.takeawayReservations.map(updateReservation);
	},
	updateSingleProductOrder(state, payload) {
		const updateProductOrderItems = (items) => {
			return items.map((item) => {
				if (payload.productOrderIdToUpdate === item.productOrderId) {
					const updatedItem = {
						...item,
						statusProductOrder: payload.status,
					};
					if (payload.status === 2 && !item.datePreparing) {
						updatedItem.datePreparing = { date: new Date().toISOString() };
					}
					return updatedItem;
				}
				return item;
			});
		};

		const updateReservationProductOrders = (reservation) => {
			if (!reservation.cartSession || !Array.isArray(reservation.cartSession.productOrders)) {
				return reservation;
			}
			return {
				...reservation,
				cartSession: {
					...reservation.cartSession,
					productOrders: reservation.cartSession.productOrders.map((productOrder) => {
						if (!Array.isArray(productOrder.items)) {
							return productOrder;
						}
						return {
							...productOrder,
							items: updateProductOrderItems(productOrder.items),
						};
					}),
				},
			};
		};

		state.reservations = state.reservations.map(updateReservationProductOrders);
		state.deliveryReservations = state.deliveryReservations.map(updateReservationProductOrders);
		state.takeawayReservations = state.takeawayReservations.map(updateReservationProductOrders);

		state.serviceEvents = state.serviceEvents.map((serviceEvent) => {
			return { ...serviceEvent, items: updateProductOrderItems(serviceEvent.items) };
		});
	},
	updateBatchProductOrders(state, payload) {
		// payload.updates is an array of { productOrderId, status }
		const updates = payload.updates;
		if (!Array.isArray(updates) || updates.length === 0) return;

		// Create a map for O(1) lookup
		const updateMap = new Map(updates.map((u) => [u.productOrderId, u.status]));

		const updateProductOrderItems = (items) => {
			return items.map((item) => {
				const newStatus = updateMap.get(item.productOrderId);
				if (newStatus !== undefined) {
					const updatedItem = { ...item, statusProductOrder: newStatus };
					if (newStatus === 2 && !item.datePreparing) {
						updatedItem.datePreparing = { date: new Date().toISOString() };
					}
					return updatedItem;
				}
				return item;
			});
		};

		const updateReservationProductOrders = (reservation) => {
			if (!reservation.cartSession || !Array.isArray(reservation.cartSession.productOrders)) {
				return reservation;
			}
			return {
				...reservation,
				cartSession: {
					...reservation.cartSession,
					productOrders: reservation.cartSession.productOrders.map((productOrder) => {
						if (!Array.isArray(productOrder.items)) {
							return productOrder;
						}
						return {
							...productOrder,
							items: updateProductOrderItems(productOrder.items),
						};
					}),
				},
			};
		};

		state.reservations = state.reservations.map(updateReservationProductOrders);
		state.deliveryReservations = state.deliveryReservations.map(updateReservationProductOrders);
		state.takeawayReservations = state.takeawayReservations.map(updateReservationProductOrders);

		state.serviceEvents = state.serviceEvents.map((serviceEvent) => {
			return { ...serviceEvent, items: updateProductOrderItems(serviceEvent.items) };
		});
	},
	addServiceEvent(state, payload) {
		state.serviceEvents.push(normalizeServiceEvent(payload.serviceEvent));
	},
	updateReservation(state, payload) {
		const updated = payload.updatedReservation;
		const updatedId = updated.realId || updated.id;
		if (!updatedId) return;

		const [enriched] = enrichReservationsServiceMode([updated], state.tables);
		state.reservations = state.reservations.map((r) => ((r.realId || r.id) === updatedId ? { ...enriched } : r));
	},
	updateDeliveryReservation(state, payload) {
		const updated = payload.updatedReservation;
		const updatedId = updated.realId || updated.id;
		if (!updatedId) return;

		const exists = state.deliveryReservations.some((r) => (r.realId || r.id) === updatedId);
		if (exists) {
			state.deliveryReservations = state.deliveryReservations.map((r) =>
				(r.realId || r.id) === updatedId ? { ...updated } : r,
			);
		} else {
			state.deliveryReservations = [...state.deliveryReservations, { ...updated }];
		}
	},
	updateTakeawayReservation(state, payload) {
		const updated = payload.updatedReservation;
		const updatedId = updated.realId || updated.id;
		if (!updatedId) return;

		const exists = state.takeawayReservations.some((r) => (r.realId || r.id) === updatedId);
		if (exists) {
			state.takeawayReservations = state.takeawayReservations.map((r) =>
				(r.realId || r.id) === updatedId ? { ...updated } : r,
			);
		} else {
			state.takeawayReservations = [...state.takeawayReservations, { ...updated }];
		}
	},
	updateServiceEvent(state, payload) {
		const normalized = normalizeServiceEvent(payload.serviceEvent);
		state.serviceEvents = state.serviceEvents.map((serviceEvent) => {
			return serviceEvent.serviceEventId === normalized.serviceEventId ? normalized : serviceEvent;
		});
	},
	addReservationToReservations(state, payload) {
		const reservation = payload.reservation;
		const newId = reservation.realId || reservation.id;
		if (!newId) {
			return;
		}

		// Drop optimistic placeholders for this table/room before inserting the real account
		// (WS often arrives while createWalkin is still in flight).
		state.reservations = state.reservations.filter(
			(r) => !r.isFictitious || !fictitiousAccountMatchesReservation(r, reservation),
		);

		const logicalDayStartHour = 6;
		const logicalDayStart = DateTime.fromFormat(state.selectedDate, 'yyyy-MM-dd').plus({ hours: logicalDayStartHour });
		const reservationTime = DateTime.fromISO(reservation.startDateTime.replace(' ', 'T'));
		const selectedDayStart = logicalDayStart.minus({ days: reservationTime.hour < logicalDayStartHour ? 1 : 0 });
		const selectedDayEnd = logicalDayStart.plus({ days: 1 });

		const isSameLogicalDay = reservationTime >= selectedDayStart && reservationTime < selectedDayEnd;

		const existingIndex = state.reservations.findIndex((r) => (r.realId || r.id) === newId);
		if (existingIndex !== -1) {
			state.reservations.splice(existingIndex, 1, { ...reservation });
			return;
		}

		if (isSameLogicalDay) {
			state.reservations.push(reservation);
		}
	},
	addDeliveryReservationToReservations(state, payload) {
		const reservation = payload.reservation;
		const newId = reservation.realId || reservation.id;
		if (!newId) return;

		// If already exists, update instead of adding duplicate
		const exists = state.deliveryReservations.some((r) => (r.realId || r.id) === newId);
		if (exists) {
			state.deliveryReservations = state.deliveryReservations.map((r) =>
				(r.realId || r.id) === newId ? { ...reservation } : r,
			);
			return;
		}

		// Check if reservation belongs to current logical day
		const logicalDayStartHour = 6;
		const logicalDayStart = DateTime.fromFormat(state.selectedDate, 'yyyy-MM-dd').plus({ hours: logicalDayStartHour });
		const reservationTime = DateTime.fromISO(reservation.startDateTime.replace(' ', 'T'));
		const selectedDayStart = logicalDayStart.minus({ days: reservationTime.hour < logicalDayStartHour ? 1 : 0 });
		const selectedDayEnd = logicalDayStart.plus({ days: 1 });

		if (reservationTime >= selectedDayStart && reservationTime < selectedDayEnd) {
			state.deliveryReservations = [...state.deliveryReservations, { ...reservation }];
		}
	},
	addTakeawayReservationToReservations(state, payload) {
		const reservation = payload.reservation;
		const newId = reservation.realId || reservation.id;
		if (!newId) return;

		const exists = state.takeawayReservations.some((r) => (r.realId || r.id) === newId);
		if (exists) {
			state.takeawayReservations = state.takeawayReservations.map((r) =>
				(r.realId || r.id) === newId ? { ...reservation } : r,
			);
			return;
		}

		const logicalDayStartHour = 6;
		const logicalDayStart = DateTime.fromFormat(state.selectedDate, 'yyyy-MM-dd').plus({ hours: logicalDayStartHour });
		const reservationTime = DateTime.fromISO(reservation.startDateTime.replace(' ', 'T'));
		const selectedDayStart = logicalDayStart.minus({ days: reservationTime.hour < logicalDayStartHour ? 1 : 0 });
		const selectedDayEnd = logicalDayStart.plus({ days: 1 });

		if (reservationTime >= selectedDayStart && reservationTime < selectedDayEnd) {
			state.takeawayReservations = [...state.takeawayReservations, { ...reservation }];
		}
	},
	addFictitiousAccount(state, fictitiousAccount) {
		state.reservations.push(fictitiousAccount);
	},
	removeFictitiousAccount(state, fictitiousAccountId) {
		const index = state.reservations.findIndex((reservation) => reservation.id === fictitiousAccountId);
		if (index !== -1) {
			state.reservations.splice(index, 1);
		}
	},
	setLocalProductIdModalReject(state, product) {
		if (!product || !product.reservationId || !product.orderId) {
			state.localProductIdModalReject = null;
			return;
		}

		const reservation = state.reservations.find((reservation) => reservation.id === product.reservationId);

		if (!reservation) {
			state.localProductIdModalReject = null;
			return;
		}

		if (reservation.cartSession) {
			for (let order of reservation.cartSession.productOrders) {
				const item = order.items.find((item) => item.productOrderId === product.orderId);

				if (item) {
					state.localProductIdModalReject = item; // Asigna directamente el producto encontrado
					return;
				}
			}
			state.localProductIdModalReject = null; // Asigna null si no se encuentra el producto
		} else {
			state.localProductIdModalReject = null;
		}
	},
	toggleProductOrderSelection(state, productOrderId) {
		const index = state.selectedProductOrdersToServe.indexOf(productOrderId);
		if (index !== -1) {
			// Deselect the product if it is already selected
			state.selectedProductOrdersToServe.splice(index, 1);
		} else {
			// Select the product if it is not yet selected
			state.selectedProductOrdersToServe.push(productOrderId);
		}
	},
	clearSelectedProductOrdersToServe(state) {
		state.selectedProductOrdersToServe = [];
	},
	toggleProductOrderToClientSelection(state, productOrderId) {
		const index = state.selectedProductOrdersToClient.indexOf(productOrderId);
		if (index !== -1) {
			// Deselect the product if it is already selected
			state.selectedProductOrdersToClient.splice(index, 1);
		} else {
			// Select the product if it is not yet selected
			state.selectedProductOrdersToClient.push(productOrderId);
		}
	},
	clearSelectedProductOrdersToClient(state) {
		state.selectedProductOrdersToClient = [];
	},
	deleteProductOrderFromSelectedProductOrdersToServe(state, payload) {
		state.selectedProductOrdersToServe = state.selectedProductOrdersToServe.filter(
			(productOrderId) => productOrderId !== payload.productOrderId,
		);
	},
	setShowAllProductOrdersInKitchen(state, payload) {
		state.showAllProductOrdersInKitchen = payload.show;
	},
	setSelectedStations(state, payload) {
		state.selectedStations = payload.selectedStations;
		normalizeShowAllProductOrdersWithStationFilters(state);
	},
	setExcludedStations(state, payload) {
		state.excludedStations = payload.excludedStations;
		normalizeShowAllProductOrdersWithStationFilters(state);
	},
	setOnlyProductOrdersWithoutElaborationStation(state, payload) {
		state.onlyProductOrdersWithoutElaborationStation = Boolean(payload?.show);
		normalizeShowAllProductOrdersWithStationFilters(state);
	},
	setShowProductOrdersWithoutElaborationWhenExcludingOnly(state, payload) {
		state.showProductOrdersWithoutElaborationWhenExcludingOnly = Boolean(payload?.show);
		normalizeShowAllProductOrdersWithStationFilters(state);
	},
	updateReservationsAfterMerging(state, payload) {
		const { reservationToUpdate, mergedReservationId } = payload;
		if (!reservationToUpdate) {
			if (mergedReservationId) {
				const filterFn = (reservations) => reservations.filter((r) => (r.realId || r.id) !== mergedReservationId);
				state.reservations = filterFn(state.reservations);
				state.deliveryReservations = filterFn(state.deliveryReservations);
				state.takeawayReservations = filterFn(state.takeawayReservations);
			}
			return;
		}

		const targetId = reservationToUpdate.realId || reservationToUpdate.id;
		const mergedId = mergedReservationId;

		const updateAndFilter = (reservations) => {
			const mapped = reservations.map((r) => ((r.realId || r.id) === targetId ? { ...reservationToUpdate } : r));

			return mapped.filter((r) => (r.realId || r.id) !== mergedId);
		};

		state.reservations = updateAndFilter(state.reservations);
		state.deliveryReservations = updateAndFilter(state.deliveryReservations);
		state.takeawayReservations = updateAndFilter(state.takeawayReservations);
	},
	reassignProductOrdersReservation(state, payload) {
		const { reassignedProductOrderIds, reservationUpdated } = payload;
		const updatedId = reservationUpdated.realId || reservationUpdated.id;

		const processReservations = (reservations) =>
			reservations
				.map((reservation) => {
					if (!reservation.cartSession || !Array.isArray(reservation.cartSession.productOrders)) {
						return reservation;
					}
					return {
						...reservation,
						cartSession: {
							...reservation.cartSession,
							productOrders: reservation.cartSession.productOrders
								.map((productOrder) => ({
									...productOrder,
									items: productOrder.items.filter((item) => !reassignedProductOrderIds.includes(item.productOrderId)),
								}))
								.filter((productOrder) => productOrder.items.length > 0),
						},
					};
				})
				.map((r) => ((r.realId || r.id) === updatedId ? { ...reservationUpdated } : r));

		state.reservations = processReservations(state.reservations);
		state.deliveryReservations = processReservations(state.deliveryReservations);
		state.takeawayReservations = processReservations(state.takeawayReservations);
	},

	SET_CONFIG(state, config) {
		state.config = config;
	},
	setSelectedZoneFilter(state, payload) {
		if (payload === null || payload === undefined) {
			state.selectedZoneFilter = [];
			localStorage.setItem('selectedZoneFilter', JSON.stringify([]));
			return;
		}
		const zones = Array.isArray(payload) ? payload.filter((z) => typeof z === 'string' && z.trim() !== '') : [];
		state.selectedZoneFilter = zones;
		localStorage.setItem('selectedZoneFilter', JSON.stringify(zones));
	},
	setSelectedAreaFilter(state, payload) {
		const filter = payload === 'table' ? 'table' : 'hour';
		state.selectedAreaFilter = filter;
		localStorage.setItem('selectedAreaFilter', filter);
	},
	setZoneFilterFromVenueConfig(state, payload) {
		if (payload && payload.copilotConfig) {
			if (typeof payload.copilotConfig.zoneFilter === 'boolean') {
				state.config.zoneFilter = payload.copilotConfig.zoneFilter;
			}
			if (typeof payload.copilotConfig.promotionsEnabled === 'boolean') {
				state.promotionsEnabled = payload.copilotConfig.promotionsEnabled;
			}
		}
	},
	setIsNotificationCustomerReadyToOrder(state, payload) {
		state.isNotificationCustomerReadyToOrder = payload;
	},
	setCurrentStaff(state, staff) {
		state.currentStaff = staff;
	},
	setStaffList(state, staffList) {
		state.staffList = staffList;
	},
	setIsKitchenShowReservationsAccount(state, payload) {
		state.isKitchenShowReservationsAccount = payload;
	},
};

const actions = {
	// changeSelectedRole({ commit }, roleId) {
	// 	commit('setSelectedRoleId', roleId);
	// },
	addProductOrder({ commit }, productOrder) {
		commit('setProductOrder', productOrder);
	},

	addReservationUserUid({ commit }, uid) {
		commit('setSelectedReservationUserId', uid);
	},

	setIsCommanderSendOrderAfterSelectTable({ commit }, payload) {
		commit('setCommanderSendOrderAfterSelectTable', payload);
	},

	async initAvailableRoles({ commit }, { user }) {
		if (user != null && user.copilotRoles != null && user.copilotRoles.length > 0) {
			commit('setAvailableRoles', { availableRoles: user.copilotRoles, isVenueMaster: true });

			user.copilotRoles.forEach((role) => {
				if (role.active === 1) {
					commit('setSelectedRoleId', { selectedRoleId: role.id });
				}
			});
		} else {
			try {
				let roleResponse = await copilotRepository.getRolesCopilot();
				commit('setAvailableRoles', { availableRoles: roleResponse.copilot_roles, isVenueMaster: true });
			} catch (e) {
				console.log('pues hay excepcion', e);
			}
		}
	},

	async getServiceRanges({ commit, state }, { venueId, force = false }) {
		// Skip if already hydrated from /copilot/init (unless forced).
		// Uses a dedicated flag so that venues with zero ranges also benefit
		// from the cache (an empty array would otherwise fail a .length > 0 guard).
		if (!force && state.serviceRangesInitialized) {
			return;
		}
		try {
			const result = await copilotRepository.getServiceRanges(venueId);
			if (result) {
				commit('setServiceRanges', result);
			}
		} catch (e) {
			console.error('Error fetching service ranges', e);
		}
	},
	async updateReservations({ commit, dispatch }) {
		try {
			commit('setIsLoadingGetReservations', { isLoadingGetReservations: true });

			const logicalDayStartHour = 5;
			const now = DateTime.now();
			const logicalToday = (now.hour < logicalDayStartHour ? now.minus({ days: 1 }) : now).toISODate();
			const isToday = state.selectedDate === logicalToday;

			// Load service events in parallel (independent of reservation loading strategy)
			dispatch('loadServiceEvents');

			if (isToday) {
				// Stage 1: active reservations only (fast — open/future/pending-elaboration)
				const activeReservations = await reservationRepository.getReservationsByScope(state.selectedDate, 'active');
				commitReservationsToStore(commit, activeReservations);
				commit('setIsLoadingGetReservations', { isLoadingGetReservations: false });

				// Stage 2: past/finished reservations in background
				dispatch('loadPastReservations');
			} else {
				// Non-today date: load all reservations in one go
				const allReservations = await reservationRepository.getReservationsByScope(state.selectedDate);
				commitReservationsToStore(commit, allReservations);
				commit('setIsLoadingGetReservations', { isLoadingGetReservations: false });
			}
		} catch (e) {
			console.error(e);
			commit('setIsLoadingGetReservations', { isLoadingGetReservations: false });
		}
	},

	async loadServiceEvents({ commit }) {
		try {
			const serviceEvents = await serviceEventRepository.getServiceEventsByDate(state.selectedDate);
			commit('setServiceEvents', { serviceEvents: Array.isArray(serviceEvents) ? serviceEvents : [] });
		} catch (e) {
			console.error('Error loading service events:', e);
		}
	},

	async loadPastReservations({ commit }) {
		try {
			commit('setIsLoadingPastReservations', { isLoadingPastReservations: true });

			const pastReservations = await reservationRepository.getReservationsByScope(state.selectedDate, 'past');

			if (!Array.isArray(pastReservations)) {
				commit('setIsLoadingPastReservations', { isLoadingPastReservations: false });
				return;
			}

			const pastLocal = pastReservations.filter(
				(reservation) =>
					reservation.type?.toLowerCase() !== state.typeAccount.delivery &&
					reservation.type?.toLowerCase() !== state.typeAccount.takeaway,
			);
			const pastDelivery = pastReservations.filter(
				(reservation) => reservation.type?.toLowerCase() === state.typeAccount.delivery,
			);
			const pastTakeaway = pastReservations.filter(
				(reservation) => reservation.type?.toLowerCase() === state.typeAccount.takeaway,
			);

			commit('mergePastReservations', { reservations: pastLocal });
			commit('mergePastDeliveryReservations', { reservations: pastDelivery });
			commit('mergePastTakeawayReservations', { reservations: pastTakeaway });
		} catch (e) {
			console.error('Error loading past reservations:', e);
			commit('setIsLoadingPastReservations', { isLoadingPastReservations: false });
		}
	},
	async updateOrdersAndNotifications({ commit }) {
		try {
			// Load notifications using the new API endpoints
			//const [recentNotifications, ticketRequestNotifications] = await Promise.all([notificationRepository.getRecentNotifications(), notificationRepository.getTicketRequestNotifications()])

			// Assign notifications: ticket-request notifications go to notifications array
			// recent notifications go to assistantNotifications array
			//commit('setNotifications', { notifications: ticketRequestNotifications })
			//commit('setAssistantNotifications', { assistantNotifications: recentNotifications })

			// Also load notifications into Pinia store for gradual migration
			try {
				const notificationStore = useNotificationStore();
				await notificationStore.loadNotifications(state.selectedDate);
				commit('setAssistantNotifications', { assistantNotifications: notificationStore.getHistoryNotifications });
				commit('setNotifications', { notifications: notificationStore.getPendingTicketNotifications });
			} catch (error) {
				console.log('Pinia notification store not available:', error);
			}
		} catch (e) {
			console.log(e);
		}
	},
	async getReservationPaymentInfo(_, data) {
		try {
			const ticketResult = await reservationRepository.getPaymentInfo(data);

			let result = ticketResult.result;
			if (result === 0) {
				return ticketResult.ticketItems;
			}

			return null;
		} catch (e) {
			console.log(e);
		}
	},
	async getBillPaymentInfo(_, data) {
		try {
			const ticketResult = await reservationRepository.getBillPaymentInfo(data);

			let result = ticketResult.result;
			if (result === 0) {
				return ticketResult;
			}

			return null;
		} catch (e) {
			console.log(e);
		}
	},
	async checkServerLogicalDate({ commit, state }) {
		try {
			const logicalDate = await configRepository.getLogicalDate();

			// Only update if we got a valid date, otherwise keep the existing value
			if (logicalDate) {
				commit('setServerLogicalDate', logicalDate);
				return logicalDate;
			} else {
				// If API call failed, return the current serverLogicalDate if it exists
				console.warn('[checkServerLogicalDate] Failed to get logical date from API, keeping existing value');
				return state.serverLogicalDate;
			}
		} catch (e) {
			console.error('[checkServerLogicalDate] Error:', e);
			// Return existing value instead of undefined
			return state.serverLogicalDate;
		}
	},
	async getBillPrintInfo(_, data) {
		try {
			const printInfoResult = await reservationRepository.getBillPrintInfo(data);

			let result = printInfoResult.result;
			if (result === 0) {
				return printInfoResult;
			}

			return null;
		} catch (e) {
			console.log(e);
		}
	},
	async loadVenueMenus({ commit, dispatch }) {
		try {
			commit('setIsLoadingVenueMenus', { isLoadingVenueMenus: true });
			await dispatch('menu/loadMenuProducts', null, { root: true });
		} catch (error) {
			console.error('Failed to fetch venue menu products', error);
		} finally {
			commit('setIsLoadingVenueMenus', { isLoadingVenueMenus: false });
		}
	},
	async getTables({ commit }) {
		try {
			const roomsAndTablesResult = await copilotRepository.getTables();
			let rooms = roomsAndTablesResult.rooms;

			if (rooms.length >= 0) {
				console.log('tables');
			} else {
				rooms = Object.values(rooms);
			}
		const tables = rooms.flatMap((room) =>
			room.tables.map((table) => ({
				...table,
				room: room.name,
				roomLabel: venueRoomLabel(room),
				roomServiceMode: room.serviceMode ?? null,
			})),
		);
			if (roomsAndTablesResult.result === 0) {
				commit('setTables', { tables: tables });
			}
		} catch (error) {
			console.error('Failed to fetch venue menus', error);
		}
	},

	getIdReservation({ commit }, id) {
		commit('setReservationId', id);
	},

	changeIsRemoveProduct({ commit }, status) {
		commit('setIsRemoveProduct', status);
	},
	saveLocalProductIdModalReject({ commit }, status) {
		commit('setLocalProductIdModalReject', status);
	},
	showToastSuccess({ commit }, payload) {
		commit('setToast', {
			toast: {
				toastMessage: payload.message,
				showToast: true,
				idToast: 'toast-success',
				type: 'success',
			},
		});
	},
	showToastWarning({ commit }, payload) {
		commit('setToast', {
			toast: {
				toastMessage: payload.message,
				showToast: true,
				idToast: 'toast-warning',
				type: 'warning',
			},
		});
	},
	showToastError({ commit }, payload) {
		commit('setToast', {
			toast: {
				toastMessage: payload.message,
				showToast: true,
				idToast: 'toast-error',
				type: 'error',
			},
		});
	},

	searchAccounts() {},
	setSelectedProductOrderToModifyPrice({ commit }, productOrder) {
		commit('setSelectedProductOrderToModifyPrice', productOrder);
	},
	async updateProductOrderPrice({ commit }, { productOrderId, newPrice }) {
		try {
			// This calls the repository which hits the backend endpoint
			const response = await copilotRepository.updateProductOrderPrice({ productOrderId, newPrice });
			if (response && (response.result === 0 || response.id)) {
				// Success
				commit('showToastSuccess', { message: 'Precio actualizado correctamente' });
				return true;
			} else {
				commit('showToastError', { message: 'Error al actualizar el precio' });
				return false;
			}
		} catch (error) {
			console.error('Error updating product order price:', error);
			commit('showToastError', { message: 'Error al actualizar el precio' });
			return false;
		}
	},
	/**
	 * Apply role filtering based on the logged-in staff's allowlist / default role.
	 *
	 * Gated by `HONOR_VENUE_OPEN_ROLE_SWITCHING` in `@/utils/staffRoles`:
	 * - `true`  → honor venue `copilotConfig.openRoleSwitching`
	 * - `false` → always lock to allowlist / default (ignore venue config)
	 *
	 * After filtering, if the currently selected role is no longer visible, the selection
	 * is reset to the first role in the filtered list.
	 */
	applyRoleFilter({ commit, state, rootGetters }) {
		const staff = state.currentStaff;
		const venueId = rootGetters['venue/venueId'];
		const allRoles = state.allVenueRoles?.length ? state.allVenueRoles : state.availableRoles;
		const openRoleSwitching = rootGetters['venue/copilotConfig']?.openRoleSwitching;
		const visibleRoles = filterRolesForStaff(allRoles, staff, venueId, { openRoleSwitching });

		commit('setAvailableRoles', { availableRoles: visibleRoles });

		const selectedStillVisible = visibleRoles.some((r) => String(r.id) === String(state.selectedRoleId));
		if (!selectedStillVisible && visibleRoles.length > 0) {
			commit('setSelectedRoleId', { selectedRoleId: visibleRoles[0].id });
		}
	},

	async loginStaff({ commit, dispatch, state, rootGetters }, { pin }) {
		try {
			const staff = state.staffList.find((s) => s.pin === pin);
			const deviceToken = deviceManager.getOrCreateToken();

			if (staff) {
				commit('setCurrentStaff', staff);

				// Apply role filtering based on staff allowlist.
				// This also resets selectedRoleId if the current one is no longer visible.
				await dispatch('applyRoleFilter');

				// Switch to the staff's default role for this venue if set and still allowed
				const venueId = rootGetters['venue/venueId'];
				const assignment = getStaffVenueAssignment(staff, venueId);
				const defaultRoleId = normalizeRoleId(
					assignment?.defaultCopilotRoleId ?? assignment?.default_copilot_role_id,
				);
				if (
					defaultRoleId != null &&
					state.availableRoles.some((role) => normalizeRoleId(role.id) === defaultRoleId)
				) {
					commit('setSelectedRoleId', { selectedRoleId: defaultRoleId });
				}

				// Fire-and-forget: log the login attempt to the backend for session tracking.
				// The backend validates the PIN against the current server state and will push
				// a force-disconnect via Mercure if the local list was stale.
				copilotRepository
					.submitAuthEvents([
						{
							clientEventId: crypto.randomUUID(),
							eventType: 'staff_login',
							staffId: staff.id ?? null,
							pin,
							loginType: 'pin',
							clientTimestamp: new Date().toISOString(),
							deviceId: deviceToken,
						},
					])
					.catch(() => {});

				return true;
			}

			// PIN not found locally — log the failed attempt for security auditing.
			copilotRepository
				.submitAuthEvents([
					{
						clientEventId: crypto.randomUUID(),
						eventType: 'pin_failure',
						clientTimestamp: new Date().toISOString(),
						deviceId: deviceToken,
						metadata: { attemptedPin: pin },
					},
				])
				.catch(() => {});

			return false;
		} catch (e) {
			console.log('Error en loginStaff:', e);
			return false;
		}
	},

	/**
	 * Log an anonymous "Entrar sin PIN" login.
	 * Does not change currentStaff (stays null). Only sends the telemetry event.
	 * Called from Copilot.vue when the user bypasses PIN entry.
	 */
	loginAnonymous() {
		const deviceToken = deviceManager.getOrCreateToken();
		copilotRepository
			.submitAuthEvents([
				{
					clientEventId: crypto.randomUUID(),
					eventType: 'staff_login',
					staffId: null,
					pin: null,
					loginType: 'anonymous',
					clientTimestamp: new Date().toISOString(),
					deviceId: deviceToken,
				},
			])
			.catch(() => {});
	},

	loginStaffFromCookie({ commit, dispatch, state }) {
		try {
			const savedPin = cookieManager.getValue('staffPin');

			if (savedPin && state.staffList.length > 0) {
				const staff = state.staffList.find((s) => s.pin === savedPin);

				if (staff) {
					commit('setCurrentStaff', staff);
					dispatch('applyRoleFilter');
					return true;
				} else {
					// El PIN guardado ya no es válido, limpiarlo
					cookieManager.setValue('staffPin', '');
				}
			}
			return false;
		} catch (e) {
			console.log('Error en loginStaffFromCookie:', e);
			return false;
		}
	},
	async fetchStaffList({ commit }) {
		try {
			const result = await copilotRepository.getStaffList();
			console.log('Resultado de API Staff:', result);

			let list = [];
			if (Array.isArray(result)) {
				list = result;
			} else if (result && result['hydra:member']) {
				list = result['hydra:member'];
			}

			commit('setStaffList', list);
			console.log('Staff list guardado en store:', list.length, 'elementos');
		} catch (e) {
			console.error('Error fetching staff list', e);
		}
	},
	logoutStaff({ commit, state }) {
		const currentStaff = state.currentStaff;
		const deviceToken = deviceManager.getOrCreateToken();

		// Fire-and-forget: notify backend so the session is closed in the DB.
		copilotRepository
			.submitAuthEvents([
				{
					clientEventId: crypto.randomUUID(),
					eventType: 'staff_logout',
					staffId: currentStaff?.id ?? null,
					clientTimestamp: new Date().toISOString(),
					deviceId: deviceToken,
				},
			])
			.catch(() => {});

		commit('setCurrentStaff', null);
		cookieManager.setValue('staffPin', '');

		// Restore the full venue role list now that no staff is logged in
		commit('setAvailableRoles', { availableRoles: state.allVenueRoles });
	},

	persistElaborationStationFilter({ state }) {
		saveCopilotStationFilterSnapshot(state.selectedRoleId, state);
	},

	/**
	 * Force-logout triggered by a Mercure push from the backend.
	 * Happens when the backend detects the device logged in with a stale staff list
	 * (e.g. a PIN was changed server-side after the last app load).
	 *
	 * Shows an informative toast and re-exposes the PIN overlay so the correct
	 * staff member can log in again.
	 */
	forceLogoutStaff({ commit, dispatch, state }, { reason = 'session_invalidated' } = {}) {
		console.warn('[Copilot] Force logout received from server. Reason:', reason);

		commit('setCurrentStaff', null);
		cookieManager.setValue('staffPin', '');

		// Restore the full venue role list
		commit('setAvailableRoles', { availableRoles: state.allVenueRoles });

		// Show a non-intrusive toast so staff understand what happened
		dispatch('showToastError', {
			message: 'Tu sesión ha sido cerrada por el sistema. Por favor vuelve a identificarte.',
		});
	},
};

export default {
	namespaced: true,
	state,
	mutations,
	getters,
	actions,
};
